import { replace } from 'connected-react-router';
import { blogs_api_get_group_subscribed_feed_url } from 'mk/urls/functions';
import { ScrollToPosition } from 'mk2/apps/groups/containers/GroupFeed/GroupFeedPage';
import { groupsSubscribedFeedUrl } from 'mk2/apps/groups/urls';
import { ComplaintEntity, ComplaintSchema } from 'mk2/containers/Complaint/Complaint.schemas';
import { handleXHRGetErrorSaga, normalizeError, XHRAction, XHRGetError } from 'mk2/helpers/api';
import { scrollElementTo } from 'mk2/helpers/scrolling';
import { removeEmpty, removePage1, url } from 'mk2/helpers/urls';
import { getLogger } from 'mk2/logger';
import {
    BlogPostEntity,
    BlogPostSchema,
    GroupMemberEntity,
    GroupMemberSchema,
} from 'mk2/schemas';
import { normalize } from 'normalizr';
import { parse } from 'query-string';
import React from 'react';
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { isNumber } from 'util';
import {
    groupSubscribedFeedFetchApi,
    groupSubscribedFeedScrollToTrigger,
    GroupSubscribedFeedFetchNormalizedResponse,
    GroupSubscribedFeedFetchTriggerAction,
    GroupSubscribedFeedScrollToTriggerAction,
    GROUP_SUBSCRIBED_FEED_FETCH_TRIGGER,
    GROUP_SUBSCRIBED_FEED_SCROLLTO_TRIGGER,
} from './GroupSubscribedFeed.actions';

const logger = getLogger('forum/GroupSubscribedFeed.sagas');

interface ApiResponseGroupSubscribedFeed {
    body: {
        moderators: GroupMemberEntity[];
        posts: BlogPostEntity[];
        complaints: ComplaintEntity[];
        count: number;
        page: number;
        numberOfPages: number;
        memberships: number[];
        groupsCount: number;
    };
}

function* fetchGroupSubscribedFeed({ currentUrl, numPostsBeforeLoad, orderBy, pageRef, xhr }: GroupSubscribedFeedFetchTriggerAction & XHRAction) {
    const qs = parse(currentUrl.search);

    if (!pageRef) {
        // if pageRef is 0, negative or invalid string, we redirect us to page=1
        const gotoUrl = url(groupsSubscribedFeedUrl, {}, removeEmpty(removePage1({ ...qs, page: 1 })));
        yield put(replace(gotoUrl));
        return;
    } else if (pageRef && qs.page && qs.page !== pageRef.toString()) {
        // if we succeed parsing incorrect string (e.g. page=3a), then redir to correct page (e.g. page=3)
        const gotoUrl = url(groupsSubscribedFeedUrl, {}, removeEmpty(removePage1({ ...qs, page: pageRef })));
        yield put(replace(gotoUrl));
        return;
    }

    if (numPostsBeforeLoad > 0 && qs.scrollto) {
        // if data is already loaded, scroll to bottom immediately
        // so that user does not experience scroll delayed by api call
        yield put(groupSubscribedFeedScrollToTrigger(qs.scrollto as ScrollToPosition));
    }

    yield put(groupSubscribedFeedFetchApi.request(orderBy, pageRef));

    try {
        const response: ApiResponseGroupSubscribedFeed = yield call(
            () => xhr.get(blogs_api_get_group_subscribed_feed_url(), removeEmpty({ orderby: orderBy, page: pageRef})),
        );

        // ak skusame nahravat zly page number (negativny, vacsi ako pocet stranok, atd.), backend nas redirectne
        if (isNumber(pageRef) && pageRef !== response.body.page) {
            throw new Error('Wrong page');
        }

        const normalizedResponse: GroupSubscribedFeedFetchNormalizedResponse = normalize({
            complaints: response.body.complaints,
            posts: response.body.posts,
            moderators: response.body.moderators,
        }, {
            complaints: [ComplaintSchema],
            posts: [BlogPostSchema],
            moderators: [GroupMemberSchema],
        });

        yield put(groupSubscribedFeedFetchApi.success(
            orderBy,
            pageRef,
            response.body.page,
            response.body.numberOfPages,
            response.body.count,
            normalizedResponse,
            response.body.memberships,
            response.body.groupsCount,
        ));

        if (pageRef === 'last') {
            // replace page='last' with real page index
            const gotoUrl = url(groupsSubscribedFeedUrl, {}, removeEmpty(removePage1({ ...qs, page: response.body.page })));
            yield put(replace(gotoUrl));
        } else if (qs.page === '1') {
            // remove ?page=1 from url
            const gotoUrl = url(groupsSubscribedFeedUrl, {}, removeEmpty(removePage1({ ...qs, page: 1 })));
            yield put(replace(gotoUrl));
        }

        const numPostsAfterLoad = response.body.posts ? response.body.posts.length : 0;
        if (numPostsBeforeLoad !== numPostsAfterLoad && qs.scrollto) {
            // other count of posts, either first load, or data on server changed, so scroll again
            yield put(groupSubscribedFeedScrollToTrigger(qs.scrollto as ScrollToPosition));
        }

    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, logger);
        yield put(groupSubscribedFeedFetchApi.failure(orderBy, pageRef, normalizeError(error)));
    }
}

function* scrolltoGroupSubscribedFeed({ scrollto }: GroupSubscribedFeedScrollToTriggerAction) {
    if (scrollto === ScrollToPosition.LastPost) {
        scrollElementTo('#scrolltoLastHere', 'window-bottom', 0);
    } else if (scrollto === ScrollToPosition.FirstPost) {
        scrollElementTo('#scrolltoFirstHere', 'window-top', 40);
    }
}

export default function* root() {
    yield all([
        // musi byt takeEvery pre GROUP_SUBSCRIBED_FEED_FETCH_TRIGGER a nie takeLatest,
        // aby sa docitali vsetky stranky, cez ktore rychlo pageujem. Inak sa killne
        // redux saga pocas jej vykonavania a loadingState ostane v stave LOADING.
        // Ked sa na nu znova vratim, uz sa nezacne znova nacitavat, lebo nie je v stave INIT
        takeEvery(GROUP_SUBSCRIBED_FEED_FETCH_TRIGGER, fetchGroupSubscribedFeed),
        takeLatest(GROUP_SUBSCRIBED_FEED_SCROLLTO_TRIGGER, scrolltoGroupSubscribedFeed),
    ]);
}
